import { useLayoutEffect } from 'react';
import type { ValueStore } from '@sorrem-client/ui/shared/value-store';
import { useStoreSelector } from './use-store-selector';

/** Reads a slice of a value store, re-rendering only when it changes by `isEqual` (see `useStoreSelector`). */
export function useValueStoreSelector<T, TSelected>(
    store: ValueStore<T>,
    selector: (value: T) => TSelected,
    isEqual?: (left: TSelected, right: TSelected) => boolean,
): TSelected {
    return useStoreSelector(store.subscribe, store.get, selector, isEqual);
}

/**
 * Calls `apply` with the store's value now and on every change, for writing
 * it to the DOM without a render. A new `apply` (memoize it on what it
 * reads) applies again at once.
 */
export function useValueStoreEffect<T>(store: ValueStore<T>, apply: (value: T) => void): void {
    // Before paint, so the first frame is already placed.
    useLayoutEffect(() => {
        apply(store.get());
        return store.subscribe(() => apply(store.get()));
    }, [store, apply]);
}
